import React from 'react';
import { TravelProfile } from '../../types/matching';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { MapPin, Globe2, Heart, MessageSquare, Calendar, Shield, CheckCircle2, Clock, XCircle, Wallet, Camera, ExternalLink, User, Sparkles } from 'lucide-react';
import { translateTravelStyle } from '../../utils/matching';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { budgetCountries } from '../../data/budget-countries';
import { useAuth } from '../../lib/auth-context';
import { openWebRedirect } from '../../lib/api';

interface MatchingCardProps {
  profile: TravelProfile;
  onLike: () => void;
  onPass: () => void;
  onMessage?: () => void;
  similarity?: number;
  isOwnProfile?: boolean;
  currentUserId?: string;
  onOpenProfile?: (profileId: string) => void;
}

export function MatchingCard({
  profile,
  onLike,
  onPass,
  onMessage,
  similarity,
  isOwnProfile,
  currentUserId,
  onOpenProfile
}: MatchingCardProps) {
  const { userInfo } = useAuth();
  const [cachedPhoto, setCachedPhoto] = React.useState<string | null>(null);
  const isLiked = currentUserId ? (profile.likes || []).includes(currentUserId) : false;
  const commentCount = profile.comments?.length || 0;
  const likesCount = profile.likes?.length || 0;
  const viewCount = profile.viewCount || 0;

  const countryFlag = React.useMemo(() => {
    if (!profile.preferences.destination) return null;
    return budgetCountries.find(c => c.code === profile.preferences.destination)?.flag || null;
  }, [profile.preferences.destination]);

  const isNewToday = React.useMemo(() => {
    if (!profile.createdAt) return false;
    const createdDate = profile.createdAt.toDate?.() || new Date(profile.createdAt);
    const today = new Date();
    return createdDate.toDateString() === today.toDateString();
  }, [profile.createdAt]);

  React.useEffect(() => {
    const fetchCachedPhoto = async () => {
      if (profile.preferences.city) {
        try {
          const cacheRef = doc(db, 'photo_cache', profile.preferences.city.toLowerCase());
          const cacheDoc = await getDoc(cacheRef);
          if (cacheDoc.exists()) {
            const data = cacheDoc.data();
            const photos = data.photos;
            if (photos && photos.length > 0) {
              const randomPhoto = photos[Math.floor(Math.random() * photos.length)];
              setCachedPhoto(randomPhoto.url);
            }
          }
        } catch (error) {
          console.error('Error fetching cached photo:', error);
        }
      }
    };
    fetchCachedPhoto();
  }, [profile.preferences.city]);

  const getDestinationImage = (countryCode: string): string => {
    const country = budgetCountries.find(c => c.code === countryCode);
    return country?.image || 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?q=80&w=2670&auto=format&fit=crop';
  };

  const getAge = (birthDate: any) => {
    if (!birthDate) return null;
    let date;
    try {
      date = birthDate.toDate?.() || new Date(birthDate);
    } catch {
      date = new Date(birthDate);
    }
    return Math.floor((new Date().getTime() - date.getTime()) / (1000 * 60 * 60 * 24 * 365.25));
  };

  const handleCardClick = () => {
    if (onOpenProfile) {
      onOpenProfile(profile.id);
    }
  };

  const handleUsernameClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openWebRedirect(`https://pvtistes.net/membre/${profile.username}/`, userInfo);
  };

  const pvtStatusConfig = {
    obtained: { label: 'Obtenu', icon: CheckCircle2, color: 'bg-emerald-500/90 text-white' },
    pending: { label: 'En cours', icon: Clock, color: 'bg-amber-500/90 text-white' },
    'not-requested': { label: 'Non demandé', icon: XCircle, color: 'bg-gray-500/80 text-white' },
  };

  const status = pvtStatusConfig[profile.pvtStatus] || pvtStatusConfig['not-requested'];
  const StatusIcon = status.icon;

  const heroImage = cachedPhoto || getDestinationImage(profile.preferences.destination);

  const departureDate = (() => {
    try {
      const d = profile.preferences.startDate.toDate?.() || new Date(profile.preferences.startDate);
      return format(d, 'MMM yyyy', { locale: fr });
    } catch {
      return null;
    }
  })();

  return (
    <div
      onClick={handleCardClick}
      className="group relative flex flex-col overflow-hidden rounded-2xl cursor-pointer bg-white dark:bg-gray-800/90 border border-gray-200/60 dark:border-gray-700/50 shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1"
    >
      {isOwnProfile && (
        <div className="absolute inset-0 rounded-2xl ring-2 ring-primary-500/30 ring-offset-0 z-10 pointer-events-none" />
      )}

      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={heroImage}
          alt={profile.preferences.city}
          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/10" />

        <div className="absolute top-3 left-3 right-3 flex items-start justify-between z-10">
          <div className="flex items-center gap-1.5 flex-wrap">
            {isNewToday && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary-500 text-white text-[10px] font-bold uppercase tracking-wider rounded-md shadow-lg">
                <Sparkles className="w-3 h-3" />
                Nouveau
              </span>
            )}
            {isOwnProfile && (
              <span className="inline-flex items-center px-2 py-1 bg-blue-500 text-white text-[10px] font-bold uppercase tracking-wider rounded-md shadow-lg">
                Toi
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            {isLiked && (
              <div className="flex items-center justify-center w-7 h-7 bg-rose-500 rounded-full shadow-lg">
                <Heart className="w-3.5 h-3.5 text-white fill-white" />
              </div>
            )}
            {profile.photos?.length > 0 && (
              <div className="flex items-center gap-1 px-2 py-1 bg-black/40 backdrop-blur-sm rounded-md text-white">
                <Camera className="w-3 h-3" />
                <span className="text-[10px] font-semibold">{profile.photos.length}</span>
              </div>
            )}
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
          <div className="flex items-end justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-lg font-bold text-white leading-tight truncate">
                {profile.firstName}{getAge(profile.birthDate) ? `, ${getAge(profile.birthDate)}` : ''}
              </h3>
              <div className="flex items-center gap-1.5 mt-1 text-white/80">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span className="text-xs font-medium truncate">{profile.location}</span>
              </div>
            </div>
            <div className="flex items-center gap-1 px-2.5 py-1 bg-white/15 backdrop-blur-sm rounded-lg border border-white/20 flex-shrink-0">
              <Globe2 className="w-3 h-3 text-white" />
              <span className="text-xs font-semibold text-white truncate max-w-[100px]">{profile.preferences.city}</span>
              {countryFlag && <span className="text-sm">{countryFlag}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col flex-1 p-4 gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold ${status.color}`}>
            <StatusIcon className="w-3 h-3" />
            {status.label}
          </span>
          {departureDate && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              <Calendar className="w-3 h-3" />
              {departureDate}
            </span>
          )}
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
            <Wallet className="w-3 h-3" />
            {profile.preferences.budget.min.toLocaleString('fr-FR')}-{profile.preferences.budget.max.toLocaleString('fr-FR')}
          </span>
        </div>

        {profile.bio && (
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-2">
            {profile.bio}
          </p>
        )}

        <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100 dark:border-gray-700/50">
          <div className="flex items-center gap-3 text-gray-400 dark:text-gray-500">
            <div className="flex items-center gap-1" title={`${viewCount} vues`}>
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <span className="text-[11px] font-medium">{viewCount}</span>
            </div>
            {likesCount > 0 && (
              <div className="flex items-center gap-1" title={`${likesCount} likes`}>
                <Heart className="w-3.5 h-3.5" />
                <span className="text-[11px] font-medium">{likesCount}</span>
              </div>
            )}
            {commentCount > 0 && (
              <div className="flex items-center gap-1" title={`${commentCount} commentaires`}>
                <MessageSquare className="w-3.5 h-3.5" />
                <span className="text-[11px] font-medium">{commentCount}</span>
              </div>
            )}
          </div>

          {profile.username && (
            <a
              href={`https://pvtistes.net/membre/${profile.username}/`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleUsernameClick}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-400 dark:text-gray-500 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
            >
              <User className="w-3 h-3" />
              Profil
              <ExternalLink className="w-2.5 h-2.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
